import { useParams, Link, Navigate } from "react-router-dom";
import { CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { CtaBanner } from "./Home";
import { SERVICES } from "../data/site";

const faqs = [
  { q: "How long does an installation take?", a: "Most residential split system installs are completed in a single day. Ducted and commercial systems vary depending on scope — we'll give you a clear timeline in your quote." },
  { q: "Do you service all makes and models?", a: "Yes. Our licensed technicians repair and maintain all major brands, whether we installed the system or not." },
  { q: "Are you licensed and insured?", a: "Absolutely. We are ARCtick licensed, VBA registered and fully insured for your peace of mind." },
  { q: "Do you offer warranties?", a: "Yes — manufacturer warranties on equipment plus our own workmanship guarantee on every installation." },
];

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;
  const related = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div data-testid="service-detail-page">
      <PageHero eyebrow="SERVICE" title={service.name} subtitle={service.tagline} image={service.hero} crumbs={[{ label: "Services", to: "/services" }, { label: service.name }]} />

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <span className="text-js-blue font-semibold text-sm tracking-wide">OVERVIEW</span>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mt-3 mb-5">{service.tagline}</h2>
            <p className="text-js-charcoal/60 leading-relaxed mb-8">{service.overview}</p>
            <h3 className="font-display font-bold text-lg mb-4">Benefits</h3>
            <ul className="space-y-3">
              {service.benefits.map((b) => (
                <li key={b} className="flex items-start gap-3"><CheckCircle2 size={20} className="text-js-blue mt-0.5 shrink-0" /><span className="text-js-charcoal/70">{b}</span></li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl overflow-hidden h-[440px] border border-black/5 shadow-[0_20px_50px_rgba(0,0,0,0.08)]">
              <img src={service.image} alt={service.name} className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 bg-js-grey">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 gap-6">
          <Reveal className="bg-white rounded-3xl p-8 border border-black/5">
            <h3 className="font-display font-bold text-xl mb-5">Common problems we fix</h3>
            <ul className="space-y-3">
              {service.problems.map((p) => (
                <li key={p} className="flex items-start gap-3"><AlertCircle size={20} className="text-js-red mt-0.5 shrink-0" /><span className="text-js-charcoal/70">{p}</span></li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.08} className="bg-white rounded-3xl p-8 border border-black/5">
            <h3 className="font-display font-bold text-xl mb-5">What's included</h3>
            <div className="flex flex-wrap gap-2.5">
              {service.points.map((p) => <span key={p} className="px-4 py-2 rounded-full bg-js-blue/10 text-js-blue text-sm font-medium">{p}</span>)}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <Reveal className="mb-10">
            <span className="text-js-blue font-semibold text-sm tracking-wide">FAQ</span>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mt-3">Frequently asked questions</h2>
          </Reveal>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-js-grey rounded-2xl border border-black/5 px-6">
                <AccordionTrigger className="text-left font-display font-semibold hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-js-charcoal/60 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="pb-24 md:pb-32 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <h3 className="font-display font-bold text-2xl mb-8">Related services</h3>
          <div className="grid md:grid-cols-3 gap-5">
            {related.map((r) => (
              <Link key={r.slug} to={`/services/${r.slug}`} className="group relative overflow-hidden rounded-3xl h-56 border border-black/5">
                <img src={r.image} alt={r.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-js-charcoal/90 to-transparent" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
                  <h4 className="font-display font-bold text-lg flex items-center gap-2">{r.name} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" /></h4>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <CtaBanner />
    </div>
  );
};

export default ServiceDetail;
